import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { LeadsService } from 'service/leads.service';

type LeadFile = File | string;

type AttachmentInput =
  | File[]
  | string[]
  | null
  | {
      added?: any[];
      removed?: any[];
    };

type PresignedResponse = {
  url?: string;
  fields?: Record<string, string>;
  key?: string;
  s3Key?: string;
  uploadUrl?: string;
};

const getAttachmentsArray = (attachments?: AttachmentInput): LeadFile[] => {
  if (!attachments) return [];

  if (typeof attachments === 'object' && !Array.isArray(attachments) && 'added' in attachments) {
    return Array.isArray(attachments.added) ? [...attachments.added] : [];
  }

  if (Array.isArray(attachments)) {
    return [...attachments];
  }

  return [];
};

export const useLeadFiles = (
  leadId?: string,
  attachments?: AttachmentInput,
  onUploadComplete?: (uploadedRefs: string[], originalFiles: LeadFile[]) => Promise<void>,
) => {
  const [selectedFiles, setSelectedFiles] = useState<LeadFile[]>(getAttachmentsArray(attachments));
  const [uploadedUrls, setUploadedUrls] = useState<LeadFile[]>(getAttachmentsArray(attachments));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mappedAttachments = getAttachmentsArray(attachments);
    setSelectedFiles(mappedAttachments);
    setUploadedUrls(mappedAttachments);
  }, [attachments]);

  const uploadSingleFile = useCallback(
    async (file: File): Promise<string> => {
      if (!leadId) throw new Error('leadId is required');

      const presigned = (await LeadsService.getAttachmentUrl(
        file.name,
        file.type,
        String(file.size),
        leadId,
      )) as PresignedResponse;

      if (presigned?.url && presigned?.fields) {
        const formData = new FormData();
        Object.entries(presigned.fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);

        await axios.post(presigned.url, formData, {
          headers: { 'Content-Type': presigned.fields['Content-Type'] || 'multipart/form-data' },
        });

        return `${presigned.fields.key}`;
      }

      if (presigned?.uploadUrl && presigned?.fields) {
        await axios.post(presigned.uploadUrl, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        return `${presigned.fields.key}`;
      }

      throw new Error('Invalid presigned response');
    },
    [leadId],
  );

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!files?.length || !leadId) return;

      const original = [...selectedFiles];
      setSelectedFiles((prev) => [...prev, ...files]);
      setLoading(true);
      try {
        const uploadedRefs = await Promise.all(files.map(uploadSingleFile));
        setUploadedUrls((prev) => [...prev, ...uploadedRefs]);

        if (onUploadComplete) {
          await onUploadComplete(uploadedRefs, original);
        }

        setSelectedFiles((prev) => {
          const withoutLocalFiles = prev.filter(
            (f) =>
              !(f instanceof File && files.some((nf) => nf.name === f.name && nf.size === f.size)),
          );
          return [...withoutLocalFiles, ...uploadedRefs];
        });
      } catch (e) {
        setSelectedFiles(original);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [leadId, onUploadComplete, selectedFiles, uploadSingleFile],
  );

  const deleteFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetFiles = useCallback(() => {
    setSelectedFiles(getAttachmentsArray(attachments));
  }, [attachments]);

  return {
    selectedFiles,
    uploadedUrls,
    setSelectedFiles,
    onDrop,
    deleteFile,
    resetFiles,
    loading,
  };
};
